import type { ProposedOrderTrackingInvoiceBillingInput } from "@redotech/admin-sdk/rpc/schema/order-tracking/order-tracking-billing-period";
import { assertNever } from "@redotech/util/type";
import type { OrderTrackingBillingValue } from "./order-tracking-billing";

export type OrderTrackingBillingPeriodSnapshot =
  ProposedOrderTrackingInvoiceBillingInput;

export type OrderTrackingBillingPeriodComparePath =
  | "perOrderSettings.enabled"
  | "perOrderSettings.includedOrders"
  | "perOrderSettings.overagePricePerOrderUsd"
  | "perShipmentSettings.enabled"
  | "perShipmentSettings.includedShipments"
  | "perShipmentSettings.overagePricePerShipmentUsd"
  | "perSmsSettings.enabled"
  | "perSmsSettings.includedCredits"
  | "perSmsSettings.overagePricePerSmsUsd"
  | "perSmsSettings.overagePricePerMmsUsd"
  | "perSmsSettings.chargeCarrierFees"
  | "perEmailSettings.enabled"
  | "perEmailSettings.includedEmails"
  | "perEmailSettings.overagePricePerEmailUsd";

const ORDER_TRACKING_BILLING_PERIOD_COMPARE_PATHS: OrderTrackingBillingPeriodComparePath[] =
  [
    "perOrderSettings.enabled",
    "perOrderSettings.includedOrders",
    "perOrderSettings.overagePricePerOrderUsd",
    "perShipmentSettings.enabled",
    "perShipmentSettings.includedShipments",
    "perShipmentSettings.overagePricePerShipmentUsd",
    "perSmsSettings.enabled",
    "perSmsSettings.includedCredits",
    "perSmsSettings.overagePricePerSmsUsd",
    "perSmsSettings.overagePricePerMmsUsd",
    "perSmsSettings.chargeCarrierFees",
    "perEmailSettings.enabled",
    "perEmailSettings.includedEmails",
    "perEmailSettings.overagePricePerEmailUsd",
  ];

function formatUsd(amount: number): string {
  return `$${amount.toFixed(6).replace(/\.?0+$/, "")}`;
}

function formatBoolean(value: boolean): string {
  return value ? "enabled" : "disabled";
}

export function formatNestedBillingFieldValue(
  path: OrderTrackingBillingPeriodComparePath,
  snapshot: OrderTrackingBillingPeriodSnapshot,
): string {
  switch (path) {
    case "perOrderSettings.enabled":
      return formatBoolean(snapshot.perOrderSettings.enabled);
    case "perOrderSettings.includedOrders":
      return String(snapshot.perOrderSettings.includedOrders);
    case "perOrderSettings.overagePricePerOrderUsd":
      return formatUsd(snapshot.perOrderSettings.overagePricePerOrderUsd);
    case "perShipmentSettings.enabled":
      return formatBoolean(snapshot.perShipmentSettings.enabled);
    case "perShipmentSettings.includedShipments":
      return String(snapshot.perShipmentSettings.includedShipments);
    case "perShipmentSettings.overagePricePerShipmentUsd":
      return formatUsd(snapshot.perShipmentSettings.overagePricePerShipmentUsd);
    case "perSmsSettings.enabled":
      return formatBoolean(snapshot.perSmsSettings.enabled);
    case "perSmsSettings.includedCredits":
      return String(snapshot.perSmsSettings.includedCredits);
    case "perSmsSettings.overagePricePerSmsUsd":
      return formatUsd(snapshot.perSmsSettings.overagePricePerSmsUsd);
    case "perSmsSettings.overagePricePerMmsUsd":
      return formatUsd(snapshot.perSmsSettings.overagePricePerMmsUsd);
    case "perSmsSettings.chargeCarrierFees":
      return formatBoolean(snapshot.perSmsSettings.chargeCarrierFees);
    case "perEmailSettings.enabled":
      return formatBoolean(snapshot.perEmailSettings.enabled);
    case "perEmailSettings.includedEmails":
      return String(snapshot.perEmailSettings.includedEmails);
    case "perEmailSettings.overagePricePerEmailUsd":
      return formatUsd(snapshot.perEmailSettings.overagePricePerEmailUsd);
    default:
      return assertNever(path);
  }
}

function orderTrackingMeterWasOrIsEnabled(
  meter:
    | "perOrderSettings"
    | "perShipmentSettings"
    | "perSmsSettings"
    | "perEmailSettings",
  current: OrderTrackingBillingValue["invoiceBilling"],
  snapshot: OrderTrackingBillingPeriodSnapshot,
): boolean {
  switch (meter) {
    case "perOrderSettings":
      return (
        current.perOrderSettings.enabled || snapshot.perOrderSettings.enabled
      );
    case "perShipmentSettings":
      return (
        current.perShipmentSettings.enabled ||
        snapshot.perShipmentSettings.enabled
      );
    case "perSmsSettings":
      return current.perSmsSettings.enabled || snapshot.perSmsSettings.enabled;
    case "perEmailSettings":
      return (
        current.perEmailSettings.enabled || snapshot.perEmailSettings.enabled
      );
    default:
      return assertNever(meter);
  }
}

function orderTrackingBillingMeterSubfieldIsInactive(
  path: OrderTrackingBillingPeriodComparePath,
  current: OrderTrackingBillingValue["invoiceBilling"],
  snapshot: OrderTrackingBillingPeriodSnapshot,
): boolean {
  switch (path) {
    case "perOrderSettings.enabled":
      return false;
    case "perOrderSettings.includedOrders":
    case "perOrderSettings.overagePricePerOrderUsd":
      return !orderTrackingMeterWasOrIsEnabled(
        "perOrderSettings",
        current,
        snapshot,
      );
    case "perShipmentSettings.enabled":
      return false;
    case "perShipmentSettings.includedShipments":
    case "perShipmentSettings.overagePricePerShipmentUsd":
      return !orderTrackingMeterWasOrIsEnabled(
        "perShipmentSettings",
        current,
        snapshot,
      );
    case "perSmsSettings.enabled":
      return false;
    case "perSmsSettings.includedCredits":
    case "perSmsSettings.overagePricePerSmsUsd":
    case "perSmsSettings.overagePricePerMmsUsd":
    case "perSmsSettings.chargeCarrierFees":
      return !orderTrackingMeterWasOrIsEnabled(
        "perSmsSettings",
        current,
        snapshot,
      );
    case "perEmailSettings.enabled":
      return false;
    case "perEmailSettings.includedEmails":
    case "perEmailSettings.overagePricePerEmailUsd":
      return !orderTrackingMeterWasOrIsEnabled(
        "perEmailSettings",
        current,
        snapshot,
      );
    default:
      return assertNever(path);
  }
}

export function orderTrackingBillingDiffersFromPeriodSnapshot(
  current: OrderTrackingBillingValue["invoiceBilling"],
  snapshot: OrderTrackingBillingPeriodSnapshot,
): boolean {
  return ORDER_TRACKING_BILLING_PERIOD_COMPARE_PATHS.some(
    (path) => !invoiceBillingValuesEqual(current, snapshot, path),
  );
}

export function invoiceBillingValuesEqual(
  current: OrderTrackingBillingValue["invoiceBilling"],
  snapshot: OrderTrackingBillingPeriodSnapshot,
  path: OrderTrackingBillingPeriodComparePath,
): boolean {
  if (orderTrackingBillingMeterSubfieldIsInactive(path, current, snapshot)) {
    return true;
  }
  switch (path) {
    case "perOrderSettings.enabled":
      return (
        current.perOrderSettings.enabled === snapshot.perOrderSettings.enabled
      );
    case "perOrderSettings.includedOrders":
      return (
        current.perOrderSettings.includedOrders ===
        snapshot.perOrderSettings.includedOrders
      );
    case "perOrderSettings.overagePricePerOrderUsd":
      return (
        current.perOrderSettings.overagePricePerOrderUsd ===
        snapshot.perOrderSettings.overagePricePerOrderUsd
      );
    case "perShipmentSettings.enabled":
      return (
        current.perShipmentSettings.enabled ===
        snapshot.perShipmentSettings.enabled
      );
    case "perShipmentSettings.includedShipments":
      return (
        current.perShipmentSettings.includedShipments ===
        snapshot.perShipmentSettings.includedShipments
      );
    case "perShipmentSettings.overagePricePerShipmentUsd":
      return (
        current.perShipmentSettings.overagePricePerShipmentUsd ===
        snapshot.perShipmentSettings.overagePricePerShipmentUsd
      );
    case "perSmsSettings.enabled":
      return current.perSmsSettings.enabled === snapshot.perSmsSettings.enabled;
    case "perSmsSettings.includedCredits":
      return (
        current.perSmsSettings.includedCredits ===
        snapshot.perSmsSettings.includedCredits
      );
    case "perSmsSettings.overagePricePerSmsUsd":
      return (
        current.perSmsSettings.overagePricePerSmsUsd ===
        snapshot.perSmsSettings.overagePricePerSmsUsd
      );
    case "perSmsSettings.overagePricePerMmsUsd":
      return (
        current.perSmsSettings.overagePricePerMmsUsd ===
        snapshot.perSmsSettings.overagePricePerMmsUsd
      );
    case "perSmsSettings.chargeCarrierFees":
      return (
        current.perSmsSettings.chargeCarrierFees ===
        snapshot.perSmsSettings.chargeCarrierFees
      );
    case "perEmailSettings.enabled":
      return (
        current.perEmailSettings.enabled === snapshot.perEmailSettings.enabled
      );
    case "perEmailSettings.includedEmails":
      return (
        current.perEmailSettings.includedEmails ===
        snapshot.perEmailSettings.includedEmails
      );
    case "perEmailSettings.overagePricePerEmailUsd":
      return (
        current.perEmailSettings.overagePricePerEmailUsd ===
        snapshot.perEmailSettings.overagePricePerEmailUsd
      );
    default:
      return assertNever(path);
  }
}

export function formatNestedBillingFieldValueFromForm(
  path: OrderTrackingBillingPeriodComparePath,
  invoiceBilling: OrderTrackingBillingValue["invoiceBilling"],
): string {
  return formatNestedBillingFieldValue(path, {
    perOrderSettings: invoiceBilling.perOrderSettings,
    perShipmentSettings: invoiceBilling.perShipmentSettings,
    perSmsSettings: invoiceBilling.perSmsSettings,
    perEmailSettings: invoiceBilling.perEmailSettings,
  });
}
